<!--
  NylonSegmentedControl — a pill of mutually exclusive options, one
  selected at a time (Front | Back).

  For a small set of short, peer choices that change a setting. Two to
  four options; a boolean on/off belongs to <NylonToggle>, and a choice
  with icons or descriptions belongs to <NylonOptionGrid>.

    <NylonSegmentedControl
      :model-value="primarySide"
      :options="[{ value: 'front', label: 'Front' }, { value: 'back', label: 'Back' }]"
      aria-labelledby="placement-heading"
      @update:model-value="onPrimarySideChange"
    />

  Accessibility: renders the WAI-ARIA radio-group pattern. The selected
  option is the one tab stop; the arrow keys move the selection (and
  focus) and wrap at the ends. Name the group from the call site with
  `aria-label` or `aria-labelledby` (both fall through to the root).

  Visual styling lives in resources/assets/scss/nylon/components/_NylonSegmentedControl.scss.
  Pattern doc: /admin/patterns/nylon-segmented-control.

  Props:
    modelValue (String) — the selected option's value.
    options    (Array)  — [{ value: String, label: String }], in display order.

  Emits:
    update:modelValue (value) — an unselected option was chosen. Not
                                emitted when the selected option is clicked.
-->
<template>
  <div class="nylon-SegmentedControl" role="radiogroup" @keydown="onKeydown">
    <button
      v-for="option in options"
      :key="option.value"
      ref="option"
      type="button"
      role="radio"
      class="nylon-SegmentedControl-option"
      :class="{ 'is-selected': option.value === modelValue }"
      :aria-checked="(option.value === modelValue).toString()"
      :tabindex="option.value === modelValue ? 0 : -1"
      @click="select(option.value)"
    >
      {{ option.label }}
    </button>
  </div>
</template>

<script>
const PREVIOUS_KEYS = ["ArrowLeft", "ArrowUp"];
const NEXT_KEYS = ["ArrowRight", "ArrowDown"];

export default {
  name: "NylonSegmentedControl",

  props: {
    modelValue: {
      type: String,
      default: "",
    },
    options: {
      type: Array,
      required: true,
      validator: (options) => options.every((option) => typeof option.value === "string" && typeof option.label === "string"),
    },
  },

  emits: ["update:modelValue"],

  methods: {
    select(value) {
      if (value !== this.modelValue) {
        this.$emit("update:modelValue", value);
      }
    },

    // Radio-group keyboard pattern: arrows move the selection, wrapping.
    onKeydown(event) {
      const step = PREVIOUS_KEYS.includes(event.key) ? -1 : NEXT_KEYS.includes(event.key) ? 1 : 0;
      if (step === 0) {
        return;
      }

      event.preventDefault();
      const current = this.options.findIndex((option) => option.value === this.modelValue);
      const next = (current + step + this.options.length) % this.options.length;
      this.select(this.options[next].value);
      this.$refs.option[next].focus();
    },
  },
};
</script>
